import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';

type BlogSection = {
  heading: string;
  paragraphs: string[];
};

type BlogPost = {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  seoDescription: string;
  keywords: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  featured?: boolean;
  sections: BlogSection[];
  actionChecklist: string[];
};

const blogPosts: BlogPost[] = [
  {
    id: 1,
    slug: 'ai-can-build-a-website-but-it-takes-a-developer-to-make-it-work-for-your-business',
    title: 'AI Can Build a Website, But It Takes a Developer to Make It Work for Your Business',
    excerpt:
      'AI can generate layouts and copy fast, but a business website still needs strategy, integrations, and conversion thinking to produce real results.',
    seoDescription:
      'Learn why AI can draft a website quickly, but a developer is still the difference between a pretty mockup and a site that earns leads.',
    keywords:
      'AI website builder, small business website developer, conversion focused web design, AI website strategy, NeuraSites AI',
    category: 'AI Strategy',
    author: 'Kyle Smith',
    date: 'March 23, 2026',
    readTime: '8 min read',
    image: '/site-images/blog-ai-can-build-a-website.webp',
    sections: [
      {
        heading: 'AI Is Great at First Drafts, Not Final Decisions',
        paragraphs: [
          'AI is useful because it removes blank-page friction. It can draft a page structure, suggest headlines, and produce starter copy in minutes. That is a real advantage for small businesses that need momentum.',
          'The problem is that generated output does not know your margins, sales process, service area, lead quality threshold, or the objections prospects bring into a buying conversation. Those are the details that separate a website that looks finished from one that actually helps the business.',
        ],
      },
      {
        heading: 'Business Websites Need Systems, Not Just Screens',
        paragraphs: [
          'A working website connects messaging, design, performance, analytics, forms, scheduling, SEO, and follow-up. If one of those parts breaks, the site still exists, but it stops performing as a reliable sales asset.',
          'That is where a developer adds value. The job is not only writing code. It is translating business goals into a site architecture that is stable, measurable, and easy to improve over time.',
        ],
      },
      {
        heading: 'The Right Positioning Is Hybrid, Not Either-Or',
        paragraphs: [
          'Small business owners do not need a lecture about whether AI is good or bad. They need someone who can use AI where it helps and override it where it fails. That is the practical middle ground.',
          'When AI is paired with strategy and development discipline, you get faster production without sacrificing trust, clarity, or conversion quality. That is the combination that makes the technology useful instead of distracting.',
        ],
      },
    ],
    actionChecklist: [
      'Audit the homepage hero and confirm it answers who you help, what you solve, and what to do next.',
      'Review your form, calendar, and analytics setup so every lead path is measurable.',
      'Use AI for faster drafts, then refine structure and messaging with business context before publishing.',
    ],
  },
  {
    id: 2,
    slug: 'why-most-ai-websites-break-and-what-a-developer-does-differently',
    title: 'Why Most AI Websites Break (And What a Developer Does Differently)',
    excerpt:
      'Most AI-built sites fail at the boring parts: consistency, performance, metadata, forms, and updates. Those are the exact areas a developer protects.',
    seoDescription:
      'See the common ways AI-generated websites break down and how a developer fixes the structural issues that cost small businesses leads.',
    keywords:
      'AI website problems, website developer for small business, broken AI websites, website performance fixes, website maintenance',
    category: 'Website Rescue',
    author: 'Kyle Smith',
    date: 'March 21, 2026',
    readTime: '9 min read',
    image: '/site-images/blog-why-most-ai-websites-break.webp',
    sections: [
      {
        heading: 'The Failure Usually Starts After Launch',
        paragraphs: [
          'AI can create a convincing homepage, but websites rarely fail because the first screen looks bad. They fail when the navigation is inconsistent, the mobile layout collapses, the contact form is unreliable, or the page becomes slow after a few content changes.',
          'Those issues rarely appear in a single prompt. They show up in the messy layer between launch and day-to-day business use, which is exactly where small companies either gain confidence or start losing trust in the site.',
        ],
      },
      {
        heading: 'Developers Build for Change, Not Just for Today',
        paragraphs: [
          'A developer thinks about how the site behaves when you add new offers, connect a CRM, swap imagery, or update copy six months from now. That means cleaner component structure, better asset handling, stronger defaults, and fewer hidden points of failure.',
          'That discipline does not always feel flashy during the first week. It becomes obvious later when the site is still fast, still clear, and still easy to improve.',
        ],
      },
      {
        heading: 'Reliability Is a Conversion Feature',
        paragraphs: [
          'Users do not separate technical quality from business credibility. If pages jump, buttons feel uncertain, or forms are awkward, they interpret that as operational sloppiness.',
          'A developer protects reliability because reliability supports trust. That makes technical cleanup one of the most direct conversion improvements a service business can buy.',
        ],
      },
    ],
    actionChecklist: [
      'Test your most important lead path on mobile from first click to submission.',
      'Check page speed, image weight, and form delivery before spending more on traffic.',
      'Create a simple maintenance list for copy edits, SEO, integrations, and broken layout checks.',
    ],
  },
  {
    id: 3,
    slug: 'im-a-web-developer-heres-what-ai-gets-wrong-about-small-business-websites',
    title: 'I’m a Web Developer, Here’s What AI Gets Wrong About Small Business Websites',
    excerpt:
      'AI often misses the business realities that matter most: local trust signals, clear offers, differentiated messaging, and real-world lead handling.',
    seoDescription:
      'A developer’s view of the biggest mistakes AI makes on small business websites, from generic messaging to weak trust signals and low-conversion structure.',
    keywords:
      'small business website mistakes, AI generated website copy, local trust signals, developer perspective website strategy',
    category: 'Developer Perspective',
    author: 'Kyle Smith',
    date: 'March 19, 2026',
    readTime: '8 min read',
    image: '/site-images/blog-ai-gets-wrong.webp',
    sections: [
      {
        heading: 'Generic Copy Sounds Fine Until It Has To Sell',
        paragraphs: [
          'AI is trained to sound complete, not necessarily specific. That is why so many generated sites end up with vague headlines about innovation, solutions, and excellence that could belong to any competitor in the market.',
          'Small business websites need sharper language. Prospects should understand the service, the outcome, and the next step almost immediately. If the message could fit ten other companies, it is not doing enough work.',
        ],
      },
      {
        heading: 'Local and Category Trust Signals Are Often Missing',
        paragraphs: [
          'AI tends to underplay the details that make people feel safe buying: location context, response time expectations, proof of results, industry-specific objections, and what happens after someone clicks the CTA.',
          'Those signals matter even more for service businesses because the purchase is personal. People are not only evaluating the website. They are deciding whether to trust the operator behind it.',
        ],
      },
      {
        heading: 'The Fix Is Better Input Plus Better Judgment',
        paragraphs: [
          'The answer is not to stop using AI. The answer is to give it stronger context and then edit with business discipline. That means clearer positioning, tighter navigation, more useful proof, and a simpler path to contact.',
          'When those pieces are handled well, AI becomes a speed multiplier. Without them, it mostly produces polished noise.',
        ],
      },
    ],
    actionChecklist: [
      'Rewrite vague claims into specific outcomes, timelines, or service promises.',
      'Add local proof such as service area, testimonials, or recent project examples.',
      'Make the primary CTA visible in the hero, service sections, and page footer.',
    ],
  },
  {
    id: 4,
    slug: 'the-gap-between-ai-and-a-real-website-and-why-it-matters-for-your-business',
    title: 'The Gap Between AI and a Real Website (And Why It Matters for Your Business)',
    excerpt:
      'There is a big difference between generated pages and a real business website: one is output, the other is an operating asset that supports sales.',
    seoDescription:
      'Understand the gap between AI-generated output and a real website that supports trust, search visibility, and lead generation for your business.',
    keywords:
      'AI website vs real website, business website strategy, lead generation website, website operating asset, small business web development',
    category: 'Conversion Strategy',
    author: 'Kyle Smith',
    date: 'March 17, 2026',
    readTime: '7 min read',
    image: '/site-images/blog-ai-can-build-a-website.webp',
    sections: [
      {
        heading: 'Generated Output Stops at Presentation',
        paragraphs: [
          'AI can give you a page. A real website also needs page relationships, internal logic, content hierarchy, and a reason each section exists. Those layers are what make the site coherent instead of decorative.',
          'When the deeper structure is weak, businesses end up with pages that look acceptable in isolation but fail to support a complete customer journey.',
        ],
      },
      {
        heading: 'Real Websites Carry Operational Weight',
        paragraphs: [
          'A business website needs to answer repetitive sales questions, qualify interest, set expectations, and reduce back-and-forth. It should improve the efficiency of the business, not create more cleanup work.',
          'That means every page should connect to a commercial purpose. If a section does not support trust, clarity, SEO, or conversion, it is probably filler.',
        ],
      },
      {
        heading: 'Closing the Gap Creates Leverage',
        paragraphs: [
          'Once the site is structured correctly, content updates get easier, SEO compounds more cleanly, and paid traffic becomes less wasteful. The site stops being a static brochure and starts working like infrastructure.',
          'That shift matters because it changes the return on every marketing dollar that touches the website afterward.',
        ],
      },
    ],
    actionChecklist: [
      'Map each page to a business purpose: trust, education, qualification, or conversion.',
      'Remove decorative sections that do not answer a buyer question or move the sale forward.',
      'Make sure navigation and calls to action support the same sales process from page to page.',
    ],
  },
  {
    id: 5,
    slug: 'ai-vs-custom-development-what-small-business-owners-actually-need',
    title: 'AI vs Custom Development: What Small Business Owners Actually Need',
    excerpt:
      'The right answer is rarely pure DIY or pure custom. Most small businesses need a smart mix of AI speed and custom development judgment.',
    seoDescription:
      'A practical framework for deciding when AI is enough, when custom development matters, and how small businesses should combine both.',
    keywords:
      'AI vs custom development, small business website decision, custom website developer, AI website builder comparison, hybrid website workflow',
    category: 'Small Business Strategy',
    author: 'Kyle Smith',
    date: 'March 15, 2026',
    readTime: '10 min read',
    image: '/site-images/blog-why-most-ai-websites-break.webp',
    sections: [
      {
        heading: 'Start With Risk, Not Technology',
        paragraphs: [
          'The best question is not “Should I use AI?” It is “How expensive is it if this website underperforms?” If the site is tied directly to lead flow, reputation, or paid traffic, the cost of a weak implementation rises quickly.',
          'That is why small brochure sites can tolerate more automation, while service businesses that depend on inbound leads usually need a more deliberate build.',
        ],
      },
      {
        heading: 'AI Wins on Speed, Custom Work Wins on Fit',
        paragraphs: [
          'AI is excellent for acceleration. It helps with rough copy, page structure ideas, and rapid iteration. Custom development matters when the site needs stronger UX, cleaner performance, better integration handling, or a sharper brand expression.',
          'The smartest path for most small businesses is to use AI to compress the first 60 percent of the work, then use custom development to make the remaining 40 percent actually valuable.',
        ],
      },
      {
        heading: 'You Need a Decision Framework, Not a Trend Opinion',
        paragraphs: [
          'If your offer is simple, your update cycle is light, and your lead flow is modest, AI-heavy may be enough. If you need stronger positioning, more trust proof, better local SEO, or tighter conversion flow, you need more hands-on development.',
          'Clarity here saves money because it keeps you from buying either an overbuilt website or an underbuilt one.',
        ],
      },
    ],
    actionChecklist: [
      'List the three business outcomes your website must support this quarter.',
      'Estimate the revenue cost of poor conversion or weak lead quality.',
      'Use AI for acceleration, then budget developer time around the highest-risk parts of the site.',
    ],
  },
  {
    id: 6,
    slug: 'why-your-website-isnt-getting-leads-from-a-developers-perspective',
    title: 'Why Your Website Isn’t Getting Leads (From a Developer’s Perspective)',
    excerpt:
      'Lead problems usually come from unclear offers, weak hierarchy, slow pages, or broken handoff systems, not from a lack of traffic alone.',
    seoDescription:
      'A developer’s diagnosis of why websites fail to generate leads, including messaging issues, technical friction, and CTA problems that quietly kill conversions.',
    keywords:
      'website not getting leads, conversion rate problems, developer website audit, lead generation website fixes, small business web conversions',
    category: 'Lead Generation',
    author: 'Kyle Smith',
    date: 'March 13, 2026',
    readTime: '9 min read',
    image: '/site-images/blog-why-your-website-isnt-getting-leads.webp',
    sections: [
      {
        heading: 'Most Sites Leak Leads Before the Form',
        paragraphs: [
          'Businesses often assume they need more traffic when the bigger issue is that existing visitors do not understand the offer quickly enough. Confusing headlines, buried proof, and too many CTA options create friction before anyone reaches the form.',
          'A website should narrow decisions, not multiply them. If users have to work hard to figure out what you do, many of them will leave before you ever get a chance to sell.',
        ],
      },
      {
        heading: 'Technical Friction Quietly Damages Trust',
        paragraphs: [
          'Slow pages, awkward spacing, broken mobile layouts, and clumsy forms make a site feel less credible. People do not always complain about those issues. They just stop moving forward.',
          'From a developer’s perspective, this is why performance, layout consistency, and clean interaction states matter so much. They are not cosmetic details. They directly influence response behavior.',
        ],
      },
      {
        heading: 'Lead Flow Has To Continue After Submission',
        paragraphs: [
          'Even when a form works, businesses still lose opportunities if the handoff is weak. No confirmation message, no follow-up expectation, and no routing into the right inbox turns a completed form into a softer lead than it should be.',
          'The website has to support the next operational step, not just collect the first click.',
        ],
      },
    ],
    actionChecklist: [
      'State your main offer in one sentence above the fold and pair it with one primary CTA.',
      'Test load speed, mobile spacing, and form success states on a real phone.',
      'Make sure every submission creates a clear response promise and lands in the right system.',
    ],
  },
  {
    id: 7,
    slug: 'you-used-ai-to-build-your-website-now-what',
    title: 'You Used AI to Build Your Website, Now What?',
    excerpt:
      'Getting a site online with AI is step one. The next step is turning it into something trustworthy, measurable, and ready for real customers.',
    seoDescription:
      'If you already used AI to launch a website, here is the practical next step: audit the site, fix weak spots, and make it ready for real customer traffic.',
    keywords:
      'AI website launched now what, improve AI generated website, website audit after AI build, small business website optimization',
    category: 'Website Rescue',
    author: 'Kyle Smith',
    date: 'March 11, 2026',
    readTime: '7 min read',
    image: '/site-images/blog-you-used-ai-now-what.webp',
    sections: [
      {
        heading: 'Launch Is the Starting Line',
        paragraphs: [
          'Many business owners feel a burst of relief once the site is live, then realize they are not sure what to monitor next. That uncertainty is normal because launch only proves the site exists. It does not prove the site works.',
          'The first month after launch should be used to tighten the core offer, test the lead path, and confirm that visitors understand where to click and why.',
        ],
      },
      {
        heading: 'Run a Practical Post-Launch Audit',
        paragraphs: [
          'Start with the basics: headline clarity, proof placement, mobile behavior, page speed, SEO metadata, and form delivery. Then move into business alignment: is the offer clear, does the CTA fit buyer intent, and is the follow-up process defined?',
          'This kind of audit turns an AI-built site from a rough draft into a usable commercial tool. It is also where the highest-leverage fixes usually show up.',
        ],
      },
      {
        heading: 'Iterate With Evidence, Not Guesswork',
        paragraphs: [
          'Once analytics and form tracking are working, you can improve the site based on real behavior. That matters because the right improvement is often obvious after watching a few visitors interact with the pages.',
          'AI gets you to version one faster. Measurement and refinement are what make version one worth keeping.',
        ],
      },
    ],
    actionChecklist: [
      'Review the site as a first-time visitor and note every moment of hesitation.',
      'Add tracking for form submissions, phone taps, and booking clicks.',
      'Make one conversion-focused change at a time so you can see what improves results.',
    ],
  },
  {
    id: 8,
    slug: 'the-right-way-to-combine-ai-and-web-development-for-small-business-success',
    title: 'The Right Way to Combine AI and Web Development for Small Business Success',
    excerpt:
      'The strongest workflow uses AI for speed and developers for judgment, structure, and quality control across the full website experience.',
    seoDescription:
      'A practical guide to combining AI and web development so small businesses get faster execution without sacrificing trust, quality, or conversion.',
    keywords:
      'combine AI and web development, AI website workflow, small business website process, AI assisted web design, developer led AI build',
    category: 'AI Strategy',
    author: 'Kyle Smith',
    date: 'March 9, 2026',
    readTime: '8 min read',
    image: '/site-images/blog-behind-the-scenes.webp',
    sections: [
      {
        heading: 'Use AI Upstream, Not Unsupervised',
        paragraphs: [
          'AI is most effective early in the process. It helps generate options, identify missing sections, draft positioning angles, and accelerate content production.',
          'It becomes much riskier when teams rely on it to make final decisions about UX, brand tone, or technical implementation without human review. That is where slippage starts.',
        ],
      },
      {
        heading: 'Developers Should Own Quality Control',
        paragraphs: [
          'A developer brings structure to the workflow. That includes component logic, page behavior, accessibility, metadata, performance, routing, and the long-term maintainability of the site.',
          'This quality-control layer is what keeps the fast parts of AI from creating expensive cleanup later.',
        ],
      },
      {
        heading: 'The Best Outcome Is Speed With Accountability',
        paragraphs: [
          'Small businesses benefit most when someone is accountable for the final result, not just the draft. AI can support that person, but it cannot replace accountability.',
          'That is why the strongest delivery model is “strategy plus AI plus development,” not “prompt plus publish.”',
        ],
      },
    ],
    actionChecklist: [
      'Use AI to produce options quickly, then choose based on business goals instead of novelty.',
      'Assign one person to own the final customer journey from headline to follow-up.',
      'Review SEO, performance, and accessibility before every major publish cycle.',
    ],
  },
  {
    id: 9,
    slug: 'behind-the-scenes-what-it-really-takes-to-build-a-website-that-converts',
    title: 'Behind the Scenes: What It Really Takes to Build a Website That Converts',
    excerpt:
      'Conversion-focused websites are built through positioning, hierarchy, proof, UX, and technical discipline working together, not through design polish alone.',
    seoDescription:
      'A behind-the-scenes look at what it really takes to build a website that converts, from positioning and proof to UX and technical execution.',
    keywords:
      'website that converts, conversion focused web design, website strategy process, lead generation website build, behind the scenes web development',
    category: 'Conversion Strategy',
    author: 'Kyle Smith',
    date: 'March 7, 2026',
    readTime: '11 min read',
    image: '/site-images/blog-behind-the-scenes.webp',
    sections: [
      {
        heading: 'Positioning Comes Before Layout',
        paragraphs: [
          'The highest-performing websites usually begin with sharper positioning, not prettier visuals. Before any section is designed, the offer has to be clear enough that a qualified visitor can quickly say, “Yes, this is for me.”',
          'That clarity shapes every page decision afterward. Without it, even a well-built layout ends up carrying weak messaging.',
        ],
      },
      {
        heading: 'Hierarchy and Proof Do Most of the Selling',
        paragraphs: [
          'Strong websites guide attention intentionally. The order of sections, the contrast between headlines and supporting copy, and the timing of testimonials or case studies all influence whether someone keeps moving.',
          'Proof is especially important because it translates claims into believable evidence. Without proof, the site asks visitors to trust you on credit.',
        ],
      },
      {
        heading: 'Technical Discipline Protects the Win',
        paragraphs: [
          'Once the strategy and design are aligned, technical execution protects the result. Clean code, fast assets, reliable forms, and stable metadata keep the experience from degrading under real traffic.',
          'That is why conversion work is not a copywriting-only problem or a coding-only problem. It is the result of both being handled well at the same time.',
        ],
      },
    ],
    actionChecklist: [
      'Clarify the primary audience and business promise before redesigning visuals.',
      'Place testimonials or proof elements near the points of highest hesitation.',
      'Treat speed, mobile polish, and form reliability as part of conversion work, not separate tasks.',
    ],
  },
  {
    id: 10,
    slug: 'dont-let-ai-cost-you-customers-what-a-developer-fixes-that-ai-misses',
    title: 'Don’t Let AI Cost You Customers: What a Developer Fixes That AI Misses',
    excerpt:
      'AI shortcuts become expensive when they create trust gaps, performance problems, or confusing user journeys that push buyers away.',
    seoDescription:
      'See how AI can quietly cost your business customers and what a developer fixes to protect trust, usability, and conversion quality.',
    keywords:
      'AI cost you customers, developer fixes AI website, website trust issues, website conversion problems, small business web developer',
    category: 'Website Rescue',
    author: 'Kyle Smith',
    date: 'March 5, 2026',
    readTime: '8 min read',
    image: '/site-images/blog-dont-let-ai-cost-you-customers.webp',
    sections: [
      {
        heading: 'Cheap Mistakes Become Expensive Fast',
        paragraphs: [
          'A weak headline, an awkward mobile layout, or a confusing call to action might not look catastrophic in isolation. But when those issues stack together, they turn into lost trust and missed inquiries.',
          'That is the hidden cost of over-automation. The business saves time up front, then pays for it later in lower conversion, weaker perception, and more manual sales friction.',
        ],
      },
      {
        heading: 'Developers Fix the Gaps Customers Actually Feel',
        paragraphs: [
          'A developer improves the invisible parts of the experience that shape buyer confidence: layout consistency, content hierarchy, response feedback, metadata, technical reliability, and performance.',
          'Customers may never describe those items in technical terms, but they react to them instantly. Cleaner experiences feel safer and easier to buy from.',
        ],
      },
      {
        heading: 'The Goal Is Not Less AI, It Is Better Outcomes',
        paragraphs: [
          'There is no advantage in rejecting tools that make production faster. The advantage is knowing where automation should stop and where professional judgment should take over.',
          'Businesses that understand that boundary get the benefits of AI without letting it dilute the customer experience.',
        ],
      },
    ],
    actionChecklist: [
      'Identify the top three trust risks on your site: messaging, design, or technical reliability.',
      'Fix the parts of the experience customers feel before adding more pages or more traffic.',
      'Use developer review as a quality filter before treating AI output as finished work.',
    ],
  },
];

const blogName = 'AI and I';
const blogDescription =
  'Straight answers for small businesses using AI, strategy, and development to build websites that actually work.';

const BlogPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';
  const { slug } = useParams();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(blogPosts.map((post) => post.category)))],
    [],
  );

  const selectedPost = blogPosts.find((post) => post.slug === slug);
  const isArticleView = Boolean(selectedPost);
  const featuredPost = blogPosts.find((post) => post.featured);

  const filteredPosts =
    selectedCategory === 'All'
      ? blogPosts.filter((post) => !post.featured)
      : blogPosts.filter((post) => post.category === selectedCategory && !post.featured);

  const similarPosts = useMemo(() => {
    if (!selectedPost) return [];
    const sameCategory = blogPosts.filter(
      (post) => post.slug !== selectedPost.slug && post.category === selectedPost.category,
    );
    const fallback = blogPosts.filter((post) => post.slug !== selectedPost.slug);
    return [...sameCategory, ...fallback].slice(0, 3);
  }, [selectedPost]);

  const pageTitle = selectedPost
    ? `${selectedPost.title} | ${blogName} | NeuraSites AI`
    : `${blogName} | NeuraSites AI`;
  const pageDescription = selectedPost?.seoDescription || blogDescription;
  const canonicalUrl = `${siteUrl}/blog${selectedPost ? `/${selectedPost.slug}` : ''}`;
  const resolveImageUrl = (image: string) => (image.startsWith('http') ? image : `${siteUrl}${image}`);
  const selectedPostImageUrl = selectedPost ? resolveImageUrl(selectedPost.image) : undefined;

  const structuredData = selectedPost
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: selectedPost.title,
        description: selectedPost.seoDescription,
        image: selectedPostImageUrl,
        author: {
          '@type': 'Person',
          name: selectedPost.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'NeuraSites AI',
          url: siteUrl,
        },
        datePublished: new Date(selectedPost.date).toISOString(),
        dateModified: new Date(selectedPost.date).toISOString(),
        mainEntityOfPage: canonicalUrl,
        keywords: selectedPost.keywords,
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: `${blogName} | NeuraSites AI`,
        description: blogDescription,
        url: `${siteUrl}/blog`,
        blogPost: blogPosts.map((post) => ({
          '@type': 'BlogPosting',
          headline: post.title,
          url: `${siteUrl}/blog/${post.slug}`,
          image: resolveImageUrl(post.image),
          datePublished: new Date(post.date).toISOString(),
          author: {
            '@type': 'Person',
            name: post.author,
          },
        })),
      };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={selectedPost?.keywords}
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        ogImage={selectedPostImageUrl}
        ogType={selectedPost ? 'article' : 'website'}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
      />
      <Header />

      <main>
        <section className="relative overflow-hidden brand-soft-surface py-20">
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('/assets/portfolio_hero-6cd8c6d8.jpg')` }}
            />
            <div className="absolute inset-0 brand-soft-overlay" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              NeruaSites Blog
              <span className="block brand-heading-accent">{blogName}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              {blogDescription}
            </p>
          </div>
        </section>

        {isArticleView && selectedPost ? (
          <>
            <section className="py-12 bg-white dark:bg-gray-900">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-5">
                  <Link to="/blog" className="text-blue-600 hover:text-blue-700">
                    ← Back to {blogName}
                  </Link>
                </div>
                <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                    {selectedPost.category}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {selectedPost.date} • {selectedPost.readTime} • {selectedPost.author}
                  </span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  {selectedPost.title}
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                  {selectedPost.excerpt}
                </p>
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-80 object-cover rounded-xl mb-10"
                />
                <div className="space-y-10">
                  {selectedPost.sections.map((section) => (
                    <section key={section.heading} className="space-y-4">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {section.heading}
                      </h3>
                      {section.paragraphs.map((paragraph) => (
                        <p
                          key={paragraph}
                          className="text-lg leading-8 text-gray-700 dark:text-gray-200"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </section>
                  ))}
                </div>

                <div className="mt-12 rounded-2xl bg-slate-50 dark:bg-gray-800 p-8 border border-slate-200 dark:border-gray-700">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Practical next moves
                  </h3>
                  <ul className="space-y-3 text-gray-700 dark:text-gray-200">
                    {selectedPost.actionChecklist.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="text-blue-600">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-gray-700 dark:text-gray-200 leading-7">
                    Need help translating AI output into a website that earns trust and leads?
                    NeuraSites AI can audit the gaps, tighten the message, and build the parts AI
                    usually misses.
                  </p>
                </div>
              </div>
            </section>

            <section className="py-8 border-y border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap gap-3 justify-center">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                        selectedCategory === category
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-16 bg-gray-50 dark:bg-gray-800">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                  Similar Posts
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {similarPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="block bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
                    >
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="p-5">
                        <p className="text-sm text-blue-600 dark:text-blue-300 mb-2">
                          {post.category}
                        </p>
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                          {post.title}
                        </h4>
                        <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3">
                          {post.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            {featuredPost && (
              <section className="py-16 bg-white dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                    <div className="lg:flex">
                      <div className="lg:w-1/2">
                        <Link to={`/blog/${featuredPost.slug}`}>
                          <img
                            src={featuredPost.image}
                            alt={featuredPost.title}
                            className="w-full h-64 lg:h-full object-cover object-top"
                          />
                        </Link>
                      </div>
                      <div className="lg:w-1/2 p-8 lg:p-12">
                        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-300 mb-3">
                          Featured entry
                        </p>
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                          <Link
                            to={`/blog/${featuredPost.slug}`}
                            className="hover:text-blue-600 transition-colors"
                          >
                            {featuredPost.title}
                          </Link>
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 text-lg mb-4">
                          {featuredPost.excerpt}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                          {featuredPost.date} • {featuredPost.readTime} • {featuredPost.category}
                        </p>
                        <Link
                          to={`/blog/${featuredPost.slug}`}
                          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                          Read Entry
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="py-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap gap-3 justify-center">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                        selectedCategory === category
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-16 bg-white dark:bg-gray-900">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="block bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group cursor-pointer"
                    >
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="p-6">
                        <p className="text-sm text-blue-600 dark:text-blue-300 mb-2">
                          {post.category}
                        </p>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          {post.date} • {post.readTime}
                        </p>
                        <span className="text-blue-600 font-medium">Read Entry →</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
